import { useState, useEffect } from 'react'
import { Star, MessageCircle, User, Calendar } from 'lucide-react'
import apiService from '../services/api'
import { toast } from 'react-toastify'

interface VendorFeedback {
  id: string
  rating: number
  review?: string
  createdAt: string
  buyer: {
    companyName: string
    firstName: string
    lastName: string
  }
  po: {
    poNumber: string
    totalAmount: number
    currency: string
  }
}

interface VendorRatingStats {
  totalReviews: number
  averageRating: number
  ratingDistribution: { [key: number]: number }
}

interface VendorRatingsProps {
  vendorId: string
  showReviews?: boolean
  maxReviews?: number
}

export default function VendorRatings({ vendorId, showReviews = true, maxReviews = 5 }: VendorRatingsProps) {
  const [stats, setStats] = useState<VendorRatingStats | null>(null)
  const [feedback, setFeedback] = useState<VendorFeedback[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingReviews, setLoadingReviews] = useState(false)
  const [showAllReviews, setShowAllReviews] = useState(false)

  useEffect(() => {
    loadRatingStats()
    if (showReviews) {
      loadRecentReviews()
    }
  }, [vendorId, showReviews])

  const loadRatingStats = async () => {
    try {
      const response = await apiService.get(`/api/post-quote-activity/vendors/${vendorId}/rating-stats`) as any
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error loading vendor rating stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadRecentReviews = async () => {
    try {
      setLoadingReviews(true)
      const response = await apiService.get(`/api/post-quote-activity/vendors/${vendorId}/feedback?limit=${maxReviews}`) as any
      if (response.success) {
        setFeedback(response.data.feedback || [])
      }
    } catch (error) {
      console.error('Error loading vendor reviews:', error)
    } finally {
      setLoadingReviews(false)
    }
  }

  const loadAllReviews = async () => {
    try {
      setLoadingReviews(true)
      const response = await apiService.get(`/api/post-quote-activity/vendors/${vendorId}/feedback?limit=50`) as any
      if (response.success) {
        setFeedback(response.data.feedback || [])
        setShowAllReviews(true)
      }
    } catch (error) {
      console.error('Error loading all vendor reviews:', error)
      toast.error('Failed to load reviews')
    } finally {
      setLoadingReviews(false)
    }
  }

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6'
    }

    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    )
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
    )
  }

  if (!stats || stats.totalReviews === 0) {
    return (
      <div className="text-gray-500 text-sm">
        <MessageCircle className="h-4 w-4 inline mr-1" />
        No reviews yet
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Rating Summary */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          {renderStars(Math.round(stats.averageRating), 'lg')}
          <span className="text-2xl font-bold text-gray-900">
            {stats.averageRating.toFixed(1)}
          </span>
        </div>
        <div className="text-sm text-gray-600">
          Based on {stats.totalReviews} review{stats.totalReviews !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Rating Distribution */}
      <div className="space-y-1">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = stats.ratingDistribution[rating] || 0
          const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0
          return (
            <div key={rating} className="flex items-center space-x-2 text-sm">
              <span className="w-8 text-right">{rating}</span>
              <Star className="h-3 w-3 text-yellow-400 fill-current" />
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <span className="w-8 text-gray-600">{count}</span>
            </div>
          )
        })}
      </div>

      {/* Recent Reviews */}
      {showReviews && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900">Recent Reviews</h4>
            {!showAllReviews && stats.totalReviews > maxReviews && (
              <button
                onClick={loadAllReviews}
                disabled={loadingReviews}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {loadingReviews ? 'Loading...' : `View all ${stats.totalReviews} reviews`}
              </button>
            )}
          </div>

          {loadingReviews ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : feedback.length > 0 ? (
            <div className="space-y-4">
              {feedback.map((review) => (
                <div key={review.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        {renderStars(review.rating, 'sm')}
                        <span className="text-sm font-medium">({review.rating}/5)</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <User className="h-3 w-3 inline mr-1" />
                        {review.buyer.companyName || `${review.buyer.firstName} ${review.buyer.lastName}`}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDate(review.createdAt)}
                    </div>
                  </div>
                  
                  {review.review && (
                    <p className="text-gray-700 text-sm mb-2">"{review.review}"</p>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    Purchase Order: {review.po.poNumber} • {formatCurrency(review.po.totalAmount, review.po.currency)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No reviews available yet.</p>
          )}
        </div>
      )}
    </div>
  )
}